/**
 * Single source of truth for /admin/** route paths in studio-web.
 *
 * Plain object + builder functions (not an enum): paths are strings, several
 * take an id parameter, and `as const` gives literal-string inference for
 * static entries without the boxing/reverse-mapping overhead of a TS enum.
 *
 * Usable from both Server and Client Components (no "use client", no hooks).
 *
 * Usage:
 *   import { ADMIN_ROUTES, adminSegment } from "@/lib/routes";
 *   <Link href={ADMIN_ROUTES.posts}>Posts</Link>
 *   router.push(ADMIN_ROUTES.post(post.id));
 *   segments.includes(adminSegment(ADMIN_ROUTES.posts)) // "posts"
 */

export const ADMIN_ROUTES = {
  root: "/admin",
  dashboard: "/admin",
  customise: "/admin/customise",
  marketing: "/admin/marketing",

  posts: "/admin/posts",
  post: (id: string) => `/admin/post/${id}` as const,
  postSettings: (id: string) => `/admin/post/${id}/settings` as const,
  postAnalytics: (id: string) => `/admin/post/${id}/analytics` as const,

  events: "/admin/events",
  event: (id: string) => `/admin/event/${id}` as const,

  coaches: "/admin/coaches",
  coach: (id: string) => `/admin/coaches/${id}` as const,

  members: "/admin/members",
  membersCancelled: "/admin/members/cancelled",
  membersTeam: "/admin/members/team",

  cohorts: "/admin/cohorts",
  cohort: (id: string) => `/admin/cohorts/${id}` as const,

  pages: "/admin/pages",

  threads: "/admin/threads",
  thread: (id: string) => `/admin/thread/${id}` as const,

  feedback: "/admin/feedback",
  feedbackInbox: "/admin/feedback/inbox",
  feedbackReview: (id: string) => `/admin/feedback/review/${id}` as const,

  collections: "/admin/collections",
  courses: "/admin/courses",
  samplePacks: "/admin/sample-packs",

  landingPage: "/admin/landing-page",
  landingPageBuilder: "/admin/landing-page/builder",

  onboarding: "/admin/onboarding",
  onboardingFlow: (flowId: string) => `/admin/onboarding/${flowId}` as const,

  plans: "/admin/plans",
  plan: (id: string) => `/admin/plans/${id}` as const,
  plansApplications: "/admin/plans/applications",
  plansPayments: "/admin/plans/payments",
  plansPayouts: "/admin/plans/payouts",
  plansSettings: "/admin/plans/settings",

  settings: "/admin/settings",
  settingsListing: "/admin/settings/listing",
  settingsAppearance: "/admin/settings/appearance",
  settingsDomains: "/admin/settings/domains",
  settingsIntegrations: "/admin/settings/integrations",
  settingsNotifications: "/admin/settings/notifications",
} as const;

/**
 * Derives the first path segment after "/admin/" from a static admin route,
 * for use in `segments.includes(...)` active-state checks. Ties the segment
 * used for matching to the same route value used for navigation, so the two
 * cannot silently drift apart.
 */
export function adminSegment(route: string): string {
  return route.replace(/^\/admin\/?/, "");
}
